/**
 * Attach account delivery window + packing slip note from Firestore users/{email}.
 * Fields: windowStart, windowEnd, packingSlipNote (same as Account Settings / Admin).
 */
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { resolveUserPhone } from './userPhoneFields';

export const ACCOUNT_DELIVERY_ENRICH_TTL_MS = 60000;

const accountEnrichCache = new Map();

function cleanEmail(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase();
}

function orderEmail(order) {
  return cleanEmail(
    order?.email ||
      order?.customerEmail ||
      order?.customer?.email ||
      order?.['Email'] ||
      '',
  );
}

function isAccountDeliveryEnriched(order) {
  return Boolean(order && order._accountDelivery && typeof order._accountDelivery === 'object');
}

export function clearAccountDeliveryEnrichCache() {
  accountEnrichCache.clear();
}

async function findUserByEmail(email) {
  if (!email) return null;
  const variants = Array.from(new Set([email, email.toLowerCase()]));
  for (const cand of variants) {
    const snap = await getDocs(query(collection(db, 'users'), where('email', '==', cand), limit(1)));
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
  }
  const byUser = await getDocs(
    query(collection(db, 'users'), where('username', '==', email), limit(1)),
  );
  if (!byUser.empty) return { id: byUser.docs[0].id, ...byUser.docs[0].data() };
  return null;
}

async function findUserByEmailCached(email) {
  const hit = accountEnrichCache.get(email);
  if (hit && Date.now() - hit.at < ACCOUNT_DELIVERY_ENRICH_TTL_MS) {
    return hit.account;
  }
  const account = await findUserByEmail(email);
  accountEnrichCache.set(email, { at: Date.now(), account });
  return account;
}

function attachAccount(order, account) {
  if (!account) return order;
  return {
    ...order,
    accountWindowStart: account.windowStart || order.accountWindowStart || '',
    accountWindowEnd: account.windowEnd || order.accountWindowEnd || '',
    accountPackingSlipNote: account.packingSlipNote || order.accountPackingSlipNote || '',
    packingSlipNote:
      order.packingSlipNote ||
      order.packing_slip_note ||
      account.packingSlipNote ||
      order.customer?.packingSlipNote ||
      '',
    windowStart: account.windowStart || order.windowStart || '',
    windowEnd: account.windowEnd || order.windowEnd || '',
    _accountDelivery: {
      windowStart: account.windowStart || '',
      windowEnd: account.windowEnd || '',
      packingSlipNote: account.packingSlipNote || '',
      companyName: account.companyName || account.company || '',
      phone: resolveUserPhone(account),
    },
  };
}

/**
 * @param {Array<object>} orders
 * @returns {Promise<Array<object>>}
 */
export async function enrichOrdersWithAccountDeliveryFields(orders) {
  const list = Array.isArray(orders) ? orders : [];
  if (list.length === 0) return list;

  const emailsNeeded = [];
  const seen = new Set();
  list.forEach((order) => {
    if (isAccountDeliveryEnriched(order)) return;
    const email = orderEmail(order);
    if (!email || seen.has(email)) return;
    seen.add(email);
    emailsNeeded.push(email);
  });

  const cache = new Map();
  await Promise.all(
    emailsNeeded.map(async (email) => {
      try {
        cache.set(email, await findUserByEmailCached(email));
      } catch (err) {
        console.warn('[enrichOrdersWithAccountDeliveryFields] user lookup failed:', email, err);
        cache.set(email, null);
      }
    }),
  );

  return list.map((order) => {
    if (isAccountDeliveryEnriched(order)) return order;
    const email = orderEmail(order);
    const account = email ? cache.get(email) : null;
    return attachAccount(order, account);
  });
}
